import type { Notification } from '../types/notification'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import * as notificationApi from '../lib/api/notifications'

export interface UseNotificationsOptions {
  pageSize?: number
  type?: string
  fromDate?: Date | string
  toDate?: Date | string
  pollingInterval?: number // in milliseconds, 0 to disable
}

export interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  isFetchingMore: boolean
  error: Error | null
  pagination: {
    page: number
    pageSize: number
    totalPages: number
    totalCount: number
    hasMore: boolean
  }
  markAsRead: (notificationId: string) => Promise<void>
  markMultipleAsRead: (notificationIds: string[]) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  deleteMultipleNotifications: (notificationIds: string[]) => Promise<void>
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const { currentWorkspace } = useWorkspace()
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const pageSize = options.pageSize || 20
  const pollingInterval = options.pollingInterval ?? 30000 // Default 30 seconds
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const hasMore = page < totalPages

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (!user)
        return

      try {
        // Only set global loading for initial load, not for pagination
        if (!append) {
          setIsLoading(true)
        }
        else {
          setIsFetchingMore(true)
        }
        setError(null)

        const params: notificationApi.GetNotificationsParams = {
          page: pageNum,
          pageSize,
          workspaceId: currentWorkspace?.id,
          type: options.type,
          fromDate: options.fromDate,
          toDate: options.toDate,
        }

        const response = await notificationApi.getNotifications(params)

        if (append) {
          setNotifications(prev => [...prev, ...response.notifications])
        }
        else {
          setNotifications(response.notifications)
        }

        setPage(response.pagination.page)
        setTotalPages(response.pagination.totalPages)
        setTotalCount(response.pagination.totalCount)

        // Only update unread count if no external polling is active
        // to avoid double-updating the count
        if (!pollingInterval || pollingInterval === 0) {
          const unreadResponse = await notificationApi.getUnreadCount(currentWorkspace?.id)
          setUnreadCount(unreadResponse.count)
        }
      }
      catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch notifications'))
        console.error('Error fetching notifications:', err)
      }
      finally {
        setIsLoading(false)
        setIsFetchingMore(false)
      }
    },
    [
      user,
      currentWorkspace?.id,
      pageSize,
      options.type,
      options.fromDate,
      options.toDate,
      pollingInterval,
    ],
  )

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    if (!user)
      return

    try {
      const response = await notificationApi.getUnreadCount(currentWorkspace?.id)
      setUnreadCount(response.count)
    }
    catch (err) {
      console.error('Error fetching unread count:', err)
    }
  }, [user, currentWorkspace?.id])

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await notificationApi.markAsRead(notificationId)

        // Check if notification was already read before decrementing
        setNotifications((prev) => {
          const notification = prev.find(n => n.id === notificationId)
          const wasUnread = notification && !notification.readAt

          // Update unread count only if notification was unread
          if (wasUnread) {
            setUnreadCount(count => Math.max(0, count - 1))
          }

          // Update notification status
          return prev.map(n =>
            n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n,
          )
        })
      }
      catch (err) {
        console.error('Error marking notification as read:', err)
        // Refresh to sync state
        await fetchNotifications(page)
      }
    },
    [page, fetchNotifications],
  )

  // Mark multiple notifications as read
  const markMultipleAsRead = useCallback(
    async (notificationIds: string[]) => {
      try {
        await notificationApi.markMultipleAsRead(notificationIds)

        // Use functional state updates to avoid race conditions
        setNotifications((prev) => {
          // Capture snapshot for count calculation
          const unreadInSelection = prev.filter(
            n => notificationIds.includes(n.id) && !n.readAt,
          ).length

          // Update unread count based on snapshot
          setUnreadCount(count => Math.max(0, count - unreadInSelection))

          // Return updated notifications
          return prev.map(n =>
            notificationIds.includes(n.id) ? { ...n, readAt: new Date().toISOString() } : n,
          )
        })
      }
      catch (err) {
        console.error('Error marking notifications as read:', err)
        // Refresh to sync state
        await fetchNotifications(page)
      }
    },
    [page, fetchNotifications],
  )

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user)
      return

    try {
      await notificationApi.markAllAsRead({
        workspaceId: currentWorkspace?.id,
        type: options.type,
        fromDate: options.fromDate,
        toDate: options.toDate,
      })

      // Optimistically update local state
      setNotifications(prev => prev.map(n => ({ ...n, readAt: new Date().toISOString() })))

      // Recalculate unread count instead of forcing to zero
      // This handles filtered views correctly
      await fetchUnreadCount()
    }
    catch (err) {
      console.error('Error marking all notifications as read:', err)
      // Refresh to sync state
      await fetchNotifications(page)
    }
  }, [
    user,
    currentWorkspace?.id,
    options.type,
    options.fromDate,
    options.toDate,
    page,
    fetchNotifications,
    fetchUnreadCount,
  ])

  // Delete single notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        await notificationApi.deleteNotification(notificationId)

        // Use functional state updates to avoid race conditions
        setNotifications((prev) => {
          // Capture snapshot for calculations
          const notificationToDelete = prev.find(n => n.id === notificationId)

          // Update counts based on snapshot
          if (notificationToDelete && !notificationToDelete.readAt) {
            setUnreadCount(count => Math.max(0, count - 1))
          }
          setTotalCount(count => Math.max(0, count - 1))

          // Return filtered notifications
          return prev.filter(n => n.id !== notificationId)
        })
      }
      catch (err) {
        console.error('Error deleting notification:', err)
        // Refresh to sync state
        await fetchNotifications(page)
      }
    },
    [page, fetchNotifications],
  )

  // Delete multiple notifications
  const deleteMultipleNotifications = useCallback(
    async (notificationIds: string[]) => {
      try {
        await notificationApi.deleteMultipleNotifications(notificationIds)

        // Use functional state updates to avoid race conditions
        setNotifications((prev) => {
          // Capture snapshot for calculations
          const notificationsToDelete = prev.filter(n => notificationIds.includes(n.id))
          const unreadToDelete = notificationsToDelete.filter(n => !n.readAt).length

          // Update counts based on snapshot
          setUnreadCount(count => Math.max(0, count - unreadToDelete))
          setTotalCount(count => Math.max(0, count - notificationIds.length))

          // Return filtered notifications
          return prev.filter(n => !notificationIds.includes(n.id))
        })
      }
      catch (err) {
        console.error('Error deleting notifications:', err)
        // Refresh to sync state
        await fetchNotifications(page)
      }
    },
    [page, fetchNotifications],
  )

  // Load more notifications (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading)
      return
    await fetchNotifications(page + 1, true)
  }, [hasMore, isLoading, page, fetchNotifications])

  // Refresh notifications
  const refresh = useCallback(async () => {
    await fetchNotifications(1, false)
  }, [fetchNotifications])

  // Initial fetch with proper dependencies
  useEffect(() => {
    if (user) {
      fetchNotifications(1)
    }
    else {
      // Clear all state when user logs out
      setNotifications([])
      setUnreadCount(0)
      setPage(1)
      setTotalPages(1)
      setTotalCount(0)
      setError(null)
    }
  }, [user?.id, currentWorkspace?.id, options.type])

  // Set up polling
  useEffect(() => {
    if (pollingInterval > 0 && user) {
      pollingIntervalRef.current = setInterval(() => {
        fetchUnreadCount()
      }, pollingInterval)
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [pollingInterval, user, fetchUnreadCount])

  return {
    notifications,
    unreadCount,
    isLoading,
    isFetchingMore,
    error,
    pagination: {
      page,
      pageSize,
      totalPages,
      totalCount,
      hasMore,
    },
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    loadMore,
    refresh,
  }
}
